<template>
    <div class="safety-password">
        <div class="title">
            <span :class="safety" class="circle-marker"></span>
            <span>{{ safetyName }}</span>
        </div>

        <div class="description">{{ safetyDesc }}</div>

        <ul class="safety-checks">
            <li :class="{ checked: lengthAccepted }">Минимум 11 символов</li>
            <li :class="{ checked: letterCaseAccepted }">С прописными и строчными буквами</li>
            <li :class="{ checked: nonLettersAccepted }">С цифрами и неалфавитными символами</li>
        </ul>

        <div class="make-more-safety">
            <div class="title">Как сделать пароль еще надежнее?</div>
            <ul class="steps">
                <li>Сделайте длину пароля 14 символов</li>
                <li>Добавьте еще несколько любых символов</li>
            </ul>
        </div>
    </div>
</template>

<script>
import { charEntropy } from '@dsee/util/strings.js';


const MIN_ENTROPY = 24;
const MEDIUM_ENTROPY = 53;

/**
 * Валидация энтропии
 */
export function entropy(threshold = MIN_ENTROPY) {
    return (value) => {
        if (!value)
            return false;

        if (typeof (value) !== 'string')
            throw new TypeError('Parameter is not a string');

        return charEntropy(value) >= threshold;
    };
}

const SAFETY_LOW = 'low';
const SAFETY_MEDIUM = 'medium';
const SAFETY_HARD = 'hard';

const nameMap = {
    [SAFETY_LOW]: 'Слабый пароль',
    [SAFETY_MEDIUM]: 'Средний пароль',
    [SAFETY_HARD]: 'Надежный пароль',
};

const descMap = {
    [SAFETY_LOW]: 'Нужен пароль посложнее',
    [SAFETY_MEDIUM]: 'Пароль можно сделать надежнее',
    [SAFETY_HARD]: 'Надежный пароль',
};

export default {
    name: 'SafetyPassword',
    props: {
        password: {
            type: String,
            required: true,
        },
    },
    computed: {
        safety() {
            const value = charEntropy(this.password);

            if (Number.isNaN(value) || value < MIN_ENTROPY)
                return SAFETY_LOW;

            if (value < MEDIUM_ENTROPY)
                return SAFETY_MEDIUM;

            return SAFETY_HARD;
        },
        safetyName() {
            return nameMap[this.safety];
        },
        safetyDesc() {
            return descMap[this.safety];
        },
        safetyLow() {
            return this.safety === SAFETY_LOW;
        },
        safetyMedium() {
            return this.safety === SAFETY_MEDIUM;
        },
        safetyHard() {
            return this.safety === SAFETY_HARD;
        },
        lengthAccepted() {
            return this.password.length >= 11;
        },
        letterCaseAccepted() {
            return /(?=.*\p{Ll})(?=.*\p{Lu})/u.test(this.password);
        },
        nonLettersAccepted() {
            return /(?=.*\d)(?=.*[^\p{L}\d])/u.test(this.password);
        },
    },
    watch: {
        safetyHard: {
            immediate: true,
            handler(value) {
                this.$emit('safetyHard', value);
            },
        },
        safetyMedium: {
            immediate: true,
            handler(value) {
                this.$emit('safetyMedium', value);
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.safety-password {
    color: rgba(16, 24, 40, 0.7);

    .circle-marker {
        margin-right: 4px;

        &.low {
            background: #FF0C0C;
        }
        &.medium {
            background: #FFB608;
        }
        &.hard {
            background: #0ACB5B;
        }
    }

    .description {
        margin-top: 8px;
        color: var(--text-color);
        font-weight: 500;
    }

    .safety-checks {
        margin: 8px 0 0 0;
        padding: 0;
        list-style-type: none;

        li {
            padding-left: 20px;
            background-image: url(./icons/check_none.svg);
            background-repeat: no-repeat;
        }

        li.checked {
            background-image: url(./icons/check_ok.svg);
        }
    }

    .make-more-safety {
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px rgba(16, 24, 40, 0.1) solid;
        font-size: 12px;

        .title {
            color: var(--text-color);
            font-weight: 500;
        }

        .steps {
            margin: 4px 0 0 0;
            padding: 0 0 0 20px;
            list-style-type: disc;
        }
    }

}
</style>
